/**
 * BookingFlows Connect — the arena shell.
 *
 * Its own room, like HELM Intelligence: glass sidebar, mobile drawer,
 * emerald identity. Everyone with a BookingFlows account gets in — captains,
 * crew, owners. Money earned here is PERSONAL (your own Stripe account).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { ArrowLeft, Menu, X, Store, Layers, Wallet, ListChecks, Waypoints, Check, Copy } from 'lucide-react';
import { connectApi } from '../../utils/apiV2';
import { toast } from 'sonner';

export const ACCENT = '#059669';
export const CARD = 'rounded-2xl border border-emerald-900/5 bg-white shadow-[0_2px_16px_-6px_rgba(5,150,105,0.12)]';

const SYM = { USD: '$', AED: 'د.إ ', GBP: '£', EUR: '€' };
export const money = (v, cur = 'USD') => `${SYM[cur] || `${cur} `}${Number(v || 0).toFixed(2)}`;

export const ConnectRoute = ({ children }) => (
  localStorage.getItem('jwtToken') ? children : <Navigate to="/login" replace />
);

let _meCache = null;
export const useConnectMe = () => {
  const [me, setMe] = useState(_meCache);
  useEffect(() => {
    if (_meCache) { setMe(_meCache); return; }
    connectApi.me().then((r) => { _meCache = r.data; setMe(r.data); }).catch(() => {});
  }, []);
  const refresh = () => connectApi.me().then((r) => { _meCache = r.data; setMe(r.data); return r.data; }).catch(() => {});
  return { me, refresh };
};

export const CopyBtn = ({ text, label = 'Copy', testid }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => {
        try { navigator.clipboard.writeText(text); } catch { /* noop */ }
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
        toast.success('Copied to clipboard');
      }}
      className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-bold px-3.5 py-2 hover:bg-emerald-700 transition-colors"
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? 'Copied' : label}
    </button>
  );
};

const NAV = [
  { id: 'marketplace', label: 'The Network', icon: Store, path: '/connect' },
  { id: 'flows', label: 'My Flows', icon: Layers, path: '/connect/flows' },
  { id: 'earnings', label: 'Earnings', icon: Wallet, path: '/connect/earnings' },
  { id: 'listings', label: 'My Listings', icon: ListChecks, path: '/connect/listings', adminOnly: true },
];

const Brand = ({ onClick, small = false }) => (
  <button onClick={onClick} className="flex items-center gap-2.5" data-testid="connect-brand">
    <span className={`${small ? 'w-8 h-8' : 'w-9 h-9'} rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center shadow-sm shadow-emerald-200`}>
      <Waypoints className="w-4.5 h-4.5 w-[18px] h-[18px]" strokeWidth={2} />
    </span>
    <span className="flex flex-col items-start leading-none">
      <span className="font-extrabold text-sm tracking-tight text-slate-900 whitespace-nowrap">
        BookingFlows <span className="text-emerald-600">Connect</span>
      </span>
      <span className="text-[9px] font-semibold text-slate-400 tracking-wide mt-0.5">The affiliate network</span>
    </span>
  </button>
);

const NavItems = ({ pathname, onNavigate, isAdmin, idPrefix = '' }) => (
  <>
    {NAV.filter((n) => !n.adminOnly || isAdmin).map((item) => {
      const Icon = item.icon;
      const active = pathname === item.path;
      return (
        <button
          key={item.id}
          data-testid={`${idPrefix}connect-nav-${item.id}`}
          onClick={() => onNavigate(item.path)}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-left transition-colors duration-150 ${
            active ? 'bg-emerald-50 text-emerald-700 font-bold' : 'text-slate-700 hover:bg-white/80 font-semibold'
          }`}
        >
          <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors duration-150 ${
            active ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600'
          }`}>
            <Icon className="w-4 h-4" strokeWidth={1.75} />
          </span>
          <span className="truncate">{item.label}</span>
        </button>
      );
    })}
  </>
);

const BackToDashboard = ({ onClick, idPrefix = '' }) => (
  <button
    data-testid={`${idPrefix}connect-back-to-dashboard`}
    onClick={onClick}
    className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-white/80 transition-colors duration-150"
  >
    <span className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
      <ArrowLeft className="w-4 h-4 text-slate-600" strokeWidth={1.75} />
    </span>
    Back to Dashboard
  </button>
);

export const ConnectShell = ({ children }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { me } = useConnectMe();
  const isAdmin = !!me?.isOrgAdmin;
  const go = (path) => { setDrawerOpen(false); navigate(path); };

  return (
    <div className="min-h-screen flex bg-[#FAFDFB] relative" style={{ fontFamily: "'Manrope', 'Inter', sans-serif" }} data-testid="connect-shell">
      <style>{`@keyframes cxRise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } } .cx-rise { animation: cxRise 0.5s cubic-bezier(0.22,1,0.36,1) both; }`}</style>

      {/* Floaty gradient backdrop */}
      <div className="fixed inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-20 -left-20 w-56 h-56 sm:w-[560px] sm:h-[560px] sm:-top-32 sm:-left-32 bg-emerald-200/25 rounded-full blur-3xl" />
        <div className="absolute top-1/3 -right-20 w-48 h-48 sm:w-[480px] sm:h-[480px] sm:-right-40 bg-teal-200/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 left-1/3 w-52 h-52 sm:w-[520px] sm:h-[520px] sm:-bottom-40 sm:left-1/4 bg-lime-200/15 rounded-full blur-3xl" />
      </div>

      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex flex-col w-64 shrink-0 bg-white/50 backdrop-blur-xl border-r border-emerald-900/5 md:sticky md:top-0 md:self-start md:h-screen relative z-10"
        data-testid="connect-sidebar"
      >
        <div className="px-4 pt-5 pb-3 shrink-0">
          <Brand onClick={() => navigate('/connect')} />
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
          <NavItems pathname={pathname} onNavigate={go} isAdmin={isAdmin} />
        </nav>
        {me?.affiliate && (
          <div className="px-4 py-3 mx-3 mb-2 rounded-xl bg-emerald-50/70 border border-emerald-100">
            <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700/70">Signed in as</p>
            <p className="text-sm font-extrabold text-slate-900 truncate">{me.affiliate.name}</p>
            <p className="text-[11px] text-slate-400 truncate">{me.affiliate.payoutReady ? '✓ Payouts active' : 'Payouts not set up yet'}</p>
          </div>
        )}
        <div className="p-3 border-t border-slate-100/70 shrink-0">
          <BackToDashboard onClick={() => navigate('/admin')} />
        </div>
      </aside>

      {/* Mobile drawer */}
      <div className={`md:hidden fixed inset-0 z-50 ${drawerOpen ? '' : 'pointer-events-none'}`} aria-hidden={!drawerOpen}>
        <div
          className={`absolute inset-0 bg-slate-900/30 backdrop-blur-sm transition-opacity duration-200 ${drawerOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setDrawerOpen(false)}
          data-testid="connect-drawer-backdrop"
        />
        <div
          className={`absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}`}
          data-testid="connect-drawer"
        >
          <div className="px-4 pt-5 pb-3 flex items-center justify-between shrink-0" style={{ paddingTop: 'max(1.25rem, env(safe-area-inset-top, 1.25rem))' }}>
            <Brand small onClick={() => go('/connect')} />
            <button
              onClick={() => setDrawerOpen(false)}
              className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600"
              data-testid="connect-drawer-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
            <NavItems pathname={pathname} onNavigate={go} isAdmin={isAdmin} idPrefix="drawer-" />
          </nav>
          <div className="p-3 border-t border-slate-100 shrink-0">
            <BackToDashboard onClick={() => navigate('/admin')} idPrefix="drawer-" />
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 min-w-0 relative z-10">
        <div className="md:hidden sticky top-0 z-30 bg-white/70 backdrop-blur-xl border-b border-emerald-900/5" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
          <div className="px-4 py-2.5 flex items-center justify-between">
            <Brand small onClick={() => navigate('/connect')} />
            <button
              onClick={() => setDrawerOpen(true)}
              className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-700"
              data-testid="connect-mobile-menu"
            >
              <Menu className="w-[18px] h-[18px]" />
            </button>
          </div>
        </div>
        {children}
      </main>
    </div>
  );
};
